import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { routes, URL_VARS } from '../../../constants/routing';
import { DEPT_ADMIN, DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN } from '../../../constants/users';
import { updateClaimLinkTemplateDetailsRequest } from '../../../store/actions/templates';
import { selectAdminType, selectUserDepartmentId } from '../../../store/selectors/auth';
import { ClaimLinkTemplateStatusEnum, IClaimLinkTemplate } from '../../../types/templates';
import notification from '../../../utils/notification';
import { hasPermission } from '../../../utils/users';

export const useTemplateCardContextMenu = (template?: IClaimLinkTemplate) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const adminType = useSelector(selectAdminType);
  const userDepartmentId = useSelector(selectUserDepartmentId);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);

  const canManage = useCallback(
    (templateToCheck: IClaimLinkTemplate) => {
      const { department_ids } = templateToCheck;
      return Boolean(
        hasPermission([ORG_ADMIN, DISTRIBUTOR, SUPER_ADMIN], adminType) ||
          (hasPermission([DEPT_ADMIN], adminType) && userDepartmentId && department_ids.includes(userDepartmentId)),
      );
    },
    [adminType, userDepartmentId],
  );

  const canManageSelectedTemplate = useMemo(() => {
    return Boolean(selectedUid && template && canManage(template));
  }, [selectedUid, template, canManage]);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, uid: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedUid(uid);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedUid(null);
  }, []);

  const handleEditClick = useCallback(() => {
    if (canManageSelectedTemplate && selectedUid) {
      const url = routes.templates.getTemplateUrl({
        type: URL_VARS.CLAIM_LINK,
        flowId: URL_VARS.EDIT,
        itemId: selectedUid,
      });
      history.push(url);
    }
    handleMenuClose();
  }, [selectedUid, history, handleMenuClose, canManageSelectedTemplate]);

  const handleToggleHiddenClick = useCallback(
    (hidden: boolean) => {
      if (canManageSelectedTemplate && selectedUid) {
        const newStatus = hidden ? ClaimLinkTemplateStatusEnum.Hidden : ClaimLinkTemplateStatusEnum.Live;
        dispatch(
          updateClaimLinkTemplateDetailsRequest({
            uid: selectedUid,
            status: newStatus,
            resolve: () =>
              notification.success(undefined, {
                content: hidden ? 'Template hidden successfully!' : 'Template made live successfully!',
              }),
            reject: () => notification.error(undefined, { content: 'Failed to update template status' }),
          }),
        );
      }
      handleMenuClose();
    },
    [selectedUid, dispatch, handleMenuClose, canManageSelectedTemplate],
  );

  return {
    menuAnchorEl,
    selectedUid,
    handleMenuOpen,
    handleMenuClose,
    handleEditClick,
    handleToggleHiddenClick,
  };
};
